import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { setAudioMuted, isAudioMuted } from "../engine/audio/labAudio";
import { buildXRLogClipboardText, captureXRLogEnvironment, clearStoredXRLog, readStoredXRLog, XR_DEBUG_LOG_KEY } from "./xrLogClipboard";
const STORAGE_KEY = "knaad-a11y";
const ALL_FLAGS = ["hi-contrast", "large-text", "reduced-motion"];
const HAND_TRACKING_STORAGE_KEY = "knaad-xr-handtracking";
const HAND_TRACKING_DOM_ATTR = "data-knaad-xr-handtracking";
const LABELS = {
    "hi-contrast": "تباين عالي",
    "large-text": "نص كبير",
    "reduced-motion": "تقليل الحركة"
};
const HINTS = {
    "hi-contrast": "نصوص أغمق وحدود أوضح",
    "large-text": "تكبير حجم الخط بنسبة 30%",
    "reduced-motion": "إيقاف الانتقالات والحركات"
};
const HAND_TRACKING_LABEL = "تتبع اليدين في VR";
const HAND_TRACKING_HINT = "تشغيل الإيماءات اليدوية على Meta Quest";
// Session 352g — molecule-cloud motion FX. Default ON. Toggleable
// for students who find the extra animation distracting.
const PHASE_MIGRATION_STORAGE_KEY = "knaad-fx-phase-migration";
const PHASE_MIGRATION_DOM_ATTR = "data-knaad-fx-phase-migration";
const PHASE_MIGRATION_LABEL = "هجرة الجزيئات بين الأطوار";
const PHASE_MIGRATION_HINT = "الراسب ينزل من المحلول للصلب، الغاز يطلع لأعلى";
const REACTION_ECHO_STORAGE_KEY = "knaad-fx-reaction-echo";
const REACTION_ECHO_DOM_ATTR = "data-knaad-fx-reaction-echo";
const REACTION_ECHO_LABEL = "صدى التفاعل المنتهي";
const REACTION_ECHO_HINT = "الحلقات تتلاشى ببطء بعد انتهاء التفاعل (2-3 ثوانٍ)";
// XR-DEBUG-COPY(343e): in-VR loggers (343c) mirror [XR] lines into
// localStorage; these labels surface a copy-to-clipboard button so a
// user who hits the Quest 3 black-screen defect can paste the log
// into chat for root-causing.
const XR_LOG_COPY_LABEL = "نسخ سجلات VR للمطور";
const XR_LOG_COPY_HINT = "انسخ سجلات [XR] الى الحافظة لارسالها للمطور";
const XR_LOG_COPY_DONE = "تم النسخ — الصق في المحادثة";
const XR_LOG_EMPTY_TOOLTIP = "لا سجلات بعد — جرب VR اولا";
const XR_LOG_FALLBACK_HINT = "حدد الكل + Cmd+C";
const XR_LOG_CLEAR_LABEL = "مسح السجلات";
const XR_LOG_CLEARED_TOAST = "تم المسح";
const COPY_DONE_RESET_MS = 2000;
const CLEAR_TOAST_RESET_MS = 1500;
// SAFETY-GATE(343b): VR enter button is hidden by default until on-device
// debugging closes the Quest 3 black-screen + frozen-session defect.
const VR_EXPERIMENTAL_STORAGE_KEY = "knaad-xr-experimental";
const VR_EXPERIMENTAL_DOM_ATTR = "data-knaad-vr-experimental";
const VR_EXPERIMENTAL_LABEL = "تفعيل VR (تجريبي)";
const VR_EXPERIMENTAL_HINT = "زر VR مخفي بسبب مشكلة شاشة سوداء على Quest 3 — فعله فقط لو كنت تختبر";
// SESSION-343D: link to bare VR test page for isolating XR root cause.
const VR_TEST_PAGE_HREF = "/vr-test.html";
const VR_TEST_PAGE_LABEL = "افتح صفحة اختبار VR";
const VR_TEST_PAGE_HINT = "مشهد VR مجرد جدا للتحقق من أن قاعدة WebXR تعمل بمعزل عن المعمل";
function readHandTracking() {
    try {
        const raw = localStorage.getItem(HAND_TRACKING_STORAGE_KEY);
        if (raw === null)
            return true;
        return raw === "1";
    }
    catch {
        return true;
    }
}
function applyHandTrackingToDom(enabled) {
    document.documentElement.setAttribute(HAND_TRACKING_DOM_ATTR, enabled ? "on" : "off");
}
function readVRExperimental() {
    try {
        // 343g: default ON per user request — they accept the Quest 3
        // black-screen risk in exchange for being able to test. The EXIT VR
        // button + 3-sec auto-recovery + server log shipper are all active.
        const raw = localStorage.getItem(VR_EXPERIMENTAL_STORAGE_KEY);
        if (raw === null)
            return true;
        return raw === "1";
    }
    catch {
        return true;
    }
}
function applyVRExperimentalToDom(enabled) {
    document.documentElement.setAttribute(VR_EXPERIMENTAL_DOM_ATTR, enabled ? "on" : "off");
}
// Session 352g — phase migration + reaction echo readers. Default ON
// per «خيارات الوصول افتراضيا متوفرة».
function readBoolDefaultOn(key) {
    try {
        const raw = localStorage.getItem(key);
        if (raw === null)
            return true;
        return raw === "1";
    }
    catch {
        return true;
    }
}
function applyOnOffAttr(attr, enabled) {
    document.documentElement.setAttribute(attr, enabled ? "on" : "off");
}
function readStored() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return new Set();
        const parts = raw
            .split(",")
            .map((p) => p.trim())
            .filter((p) => ALL_FLAGS.includes(p));
        return new Set(parts);
    }
    catch {
        return new Set();
    }
}
function applyToDom(flags) {
    const value = Array.from(flags).join(" ");
    const root = document.documentElement;
    if (value)
        root.setAttribute("data-knaad-a11y", value);
    else
        root.removeAttribute("data-knaad-a11y");
}
const AUDIO_MUTE_KEY = "knaad-audio-muted";
// Session 352i — dev tooling stays out of the public a11y panel.
// Surface XR-log copy/clear + the bare VR test page only when the
// URL carries `?xrDebug=1` (same convention as the in-VR HUD). The
// user hits this when they want to capture logs for the developer;
// everyone else sees a clean settings panel.
function isXRDebugMode() {
    try {
        if (typeof window === "undefined")
            return false;
        return new URLSearchParams(window.location.search).get("xrDebug") === "1";
    }
    catch {
        return false;
    }
}
export function A11ySettings() {
    const [open, setOpen] = useState(false);
    const xrDebug = isXRDebugMode();
    const [flags, setFlags] = useState(() => new Set());
    const [audioMuted, setAudioMutedState] = useState(false);
    const [handTracking, setHandTracking] = useState(true);
    const [vrExperimental, setVRExperimental] = useState(false);
    const [phaseMigration, setPhaseMigration] = useState(true);
    const [reactionEcho, setReactionEcho] = useState(true);
    const [xrLogCount, setXRLogCount] = useState(0);
    const [copyDone, setCopyDone] = useState(false);
    const [clearToast, setClearToast] = useState(false);
    const [fallbackText, setFallbackText] = useState(null);
    const refreshXRLogCount = () => {
        setXRLogCount(readStoredXRLog().length);
    };
    useEffect(() => {
        const initial = readStored();
        setFlags(initial);
        applyToDom(initial);
        try {
            const muted = localStorage.getItem(AUDIO_MUTE_KEY) === "1";
            if (muted)
                setAudioMuted(true);
            setAudioMutedState(isAudioMuted());
        }
        catch { /* storage unavailable */ }
        const ht = readHandTracking();
        setHandTracking(ht);
        applyHandTrackingToDom(ht);
        const vr = readVRExperimental();
        setVRExperimental(vr);
        applyVRExperimentalToDom(vr);
        const pm = readBoolDefaultOn(PHASE_MIGRATION_STORAGE_KEY);
        setPhaseMigration(pm);
        applyOnOffAttr(PHASE_MIGRATION_DOM_ATTR, pm);
        const re = readBoolDefaultOn(REACTION_ECHO_STORAGE_KEY);
        setReactionEcho(re);
        applyOnOffAttr(REACTION_ECHO_DOM_ATTR, re);
        refreshXRLogCount();
        // Re-poll when the panel opens (cheap; only every dialog open).
        const onStorage = (e) => {
            if (e.key === XR_DEBUG_LOG_KEY)
                refreshXRLogCount();
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);
    useEffect(() => {
        if (!open)
            return;
        refreshXRLogCount();
    }, [open]);
    const handleCopyXRLog = async () => {
        const entries = readStoredXRLog();
        const text = buildXRLogClipboardText(entries, captureXRLogEnvironment());
        try {
            if (typeof navigator !== "undefined" &&
                navigator.clipboard &&
                typeof navigator.clipboard.writeText === "function") {
                await navigator.clipboard.writeText(text);
                setCopyDone(true);
                window.setTimeout(() => setCopyDone(false), COPY_DONE_RESET_MS);
                return;
            }
            throw new Error("clipboard-unavailable");
        }
        catch {
            setFallbackText(text);
        }
    };
    const handleClearXRLog = () => {
        clearStoredXRLog();
        refreshXRLogCount();
        setClearToast(true);
        window.setTimeout(() => setClearToast(false), CLEAR_TOAST_RESET_MS);
    };
    const toggleAudioMute = () => {
        const next = !audioMuted;
        setAudioMuted(next);
        setAudioMutedState(next);
        try {
            localStorage.setItem(AUDIO_MUTE_KEY, next ? "1" : "0");
        }
        catch { /* storage unavailable */ }
    };
    const toggleHandTracking = () => {
        setHandTracking((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(HAND_TRACKING_STORAGE_KEY, next ? "1" : "0");
            }
            catch {
                /* storage unavailable — DOM still updates */
            }
            applyHandTrackingToDom(next);
            return next;
        });
    };
    const toggleVRExperimental = () => {
        setVRExperimental((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(VR_EXPERIMENTAL_STORAGE_KEY, next ? "1" : "0");
            }
            catch {
                /* storage unavailable — DOM still updates */
            }
            applyVRExperimentalToDom(next);
            return next;
        });
    };
    const togglePhaseMigration = () => {
        setPhaseMigration((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(PHASE_MIGRATION_STORAGE_KEY, next ? "1" : "0");
            }
            catch { /* storage unavailable */ }
            applyOnOffAttr(PHASE_MIGRATION_DOM_ATTR, next);
            return next;
        });
    };
    const toggleReactionEcho = () => {
        setReactionEcho((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(REACTION_ECHO_STORAGE_KEY, next ? "1" : "0");
            }
            catch { /* storage unavailable */ }
            applyOnOffAttr(REACTION_ECHO_DOM_ATTR, next);
            return next;
        });
    };
    const toggle = (f) => {
        setFlags((prev) => {
            const next = new Set(prev);
            if (next.has(f))
                next.delete(f);
            else
                next.add(f);
            try {
                localStorage.setItem(STORAGE_KEY, Array.from(next).join(","));
            }
            catch {
                /* storage unavailable — DOM still updates */
            }
            applyToDom(next);
            return next;
        });
    };
    return (_jsxs(_Fragment, { children: [_jsxs("button", { type: "button", className: "a11y-fab", onClick: () => setOpen(true), title: "\u0625\u0639\u062F\u0627\u062F\u0627\u062A \u0627\u0644\u0648\u0635\u0648\u0644", "aria-label": "\u0625\u0639\u062F\u0627\u062F\u0627\u062A \u0627\u0644\u0648\u0635\u0648\u0644", children: [_jsx("span", { className: "a11y-fab-icon", "aria-hidden": "true", children: "\u267F" }), _jsx("span", { className: "a11y-fab-label", children: "\u0625\u0639\u062F\u0627\u062F\u0627\u062A \u0627\u0644\u0648\u0635\u0648\u0644" })] }), open && (_jsx("div", { className: "a11y-overlay", role: "dialog", "aria-modal": "true", "aria-label": "\u0625\u0639\u062F\u0627\u062F\u0627\u062A \u0627\u0644\u0648\u0635\u0648\u0644", onClick: () => setOpen(false), children: _jsxs("div", { className: "a11y-panel", onClick: (e) => e.stopPropagation(), children: [_jsx("button", { type: "button", className: "a11y-close", onClick: () => setOpen(false), title: "\u0625\u063A\u0644\u0627\u0642", "aria-label": "\u0625\u063A\u0644\u0627\u0642", children: "\u00D7" }), _jsx("h2", { className: "a11y-title", children: "\u0625\u0639\u062F\u0627\u062F\u0627\u062A \u0627\u0644\u0648\u0635\u0648\u0644" }), _jsx("p", { className: "a11y-sub", children: "\u064A\u0645\u0643\u0646 \u062A\u0641\u0639\u064A\u0644 \u0623\u0643\u062B\u0631 \u0645\u0646 \u062E\u064A\u0627\u0631 \u0641\u064A \u0646\u0641\u0633 \u0627\u0644\u0648\u0642\u062A. \u062A\u062D\u0641\u0638 \u0627\u0644\u0625\u0639\u062F\u0627\u062F\u0627\u062A \u062A\u0644\u0642\u0627\u0626\u064A\u0627." }), _jsxs("ul", { className: "a11y-list", children: [ALL_FLAGS.map((f) => (_jsx("li", { className: "a11y-item", children: _jsxs("label", { className: "a11y-toggle", children: [_jsx("input", { type: "checkbox", checked: flags.has(f), onChange: () => toggle(f) }), _jsxs("span", { className: "a11y-toggle-text", children: [_jsx("span", { className: "a11y-toggle-label", children: LABELS[f] }), _jsx("span", { className: "a11y-toggle-hint", children: HINTS[f] })] })] }) }, f))), _jsx("li", { className: "a11y-item", children: _jsxs("label", { className: "a11y-toggle", children: [_jsx("input", { type: "checkbox", checked: audioMuted, onChange: toggleAudioMute }), _jsxs("span", { className: "a11y-toggle-text", children: [_jsx("span", { className: "a11y-toggle-label", children: "\u0643\u062A\u0645 \u0623\u0635\u0648\u0627\u062A \u0627\u0644\u0645\u0639\u0645\u0644" }), _jsx("span", { className: "a11y-toggle-hint", children: "\u0625\u064A\u0642\u0627\u0641 \u0627\u0644\u0645\u0624\u062B\u0631\u0627\u062A \u0627\u0644\u0635\u0648\u062A\u064A\u0629 \u0643\u0644\u0647\u0627" })] })] }) }), _jsx("li", { className: "a11y-item", children: _jsxs("label", { className: "a11y-toggle", children: [_jsx("input", { type: "checkbox", checked: phaseMigration, onChange: togglePhaseMigration }), _jsxs("span", { className: "a11y-toggle-text", children: [_jsx("span", { className: "a11y-toggle-label", children: PHASE_MIGRATION_LABEL }), _jsx("span", { className: "a11y-toggle-hint", children: PHASE_MIGRATION_HINT })] })] }) }), _jsx("li", { className: "a11y-item", children: _jsxs("label", { className: "a11y-toggle", children: [_jsx("input", { type: "checkbox", checked: reactionEcho, onChange: toggleReactionEcho }), _jsxs("span", { className: "a11y-toggle-text", children: [_jsx("span", { className: "a11y-toggle-label", children: REACTION_ECHO_LABEL }), _jsx("span", { className: "a11y-toggle-hint", children: REACTION_ECHO_HINT })] })] }) }), _jsx("li", { className: "a11y-item", "data-i18n-key": "settings.xr.handTracking", children: _jsxs("label", { className: "a11y-toggle", children: [_jsx("input", { type: "checkbox", checked: handTracking, onChange: toggleHandTracking }), _jsxs("span", { className: "a11y-toggle-text", children: [_jsx("span", { className: "a11y-toggle-label", children: HAND_TRACKING_LABEL }), _jsx("span", { className: "a11y-toggle-hint", children: HAND_TRACKING_HINT })] })] }) }), _jsx("li", { className: "a11y-item", "data-i18n-key": "settings.xr.experimental", children: _jsxs("label", { className: "a11y-toggle", children: [_jsx("input", { type: "checkbox", checked: vrExperimental, onChange: toggleVRExperimental }), _jsxs("span", { className: "a11y-toggle-text", children: [_jsx("span", { className: "a11y-toggle-label", children: VR_EXPERIMENTAL_LABEL }), _jsx("span", { className: "a11y-toggle-hint", children: VR_EXPERIMENTAL_HINT })] })] }) }), xrDebug && (_jsxs("li", { className: "a11y-item", "data-i18n-key": "settings.xr.copyDebugLog", children: [_jsx("button", { type: "button", className: "a11y-action", onClick: () => { void handleCopyXRLog(); }, disabled: xrLogCount === 0, title: xrLogCount === 0 ? XR_LOG_EMPTY_TOOLTIP : XR_LOG_COPY_HINT, "aria-label": XR_LOG_COPY_LABEL, children: copyDone ? `✓ ${XR_LOG_COPY_DONE}` : XR_LOG_COPY_LABEL }), _jsx("button", { type: "button", className: "a11y-action a11y-action-secondary", onClick: handleClearXRLog, disabled: xrLogCount === 0, "aria-label": XR_LOG_CLEAR_LABEL, children: clearToast ? XR_LOG_CLEARED_TOAST : XR_LOG_CLEAR_LABEL })] })), xrDebug && (_jsx("li", { className: "a11y-item", "data-i18n-key": "settings.xr.testPage", children: _jsx("a", { className: "a11y-toggle", href: VR_TEST_PAGE_HREF, style: { textDecoration: "none", color: "inherit" }, children: _jsxs("span", { className: "a11y-toggle-text", children: [_jsx("span", { className: "a11y-toggle-label", children: VR_TEST_PAGE_LABEL }), _jsx("span", { className: "a11y-toggle-hint", children: VR_TEST_PAGE_HINT })] }) }) }))] })] }) })), fallbackText !== null && (_jsx("div", { className: "a11y-overlay", role: "dialog", "aria-modal": "true", "aria-label": XR_LOG_COPY_LABEL, onClick: () => setFallbackText(null), children: _jsxs("div", { className: "a11y-panel", onClick: (e) => e.stopPropagation(), children: [_jsx("button", { type: "button", className: "a11y-close", onClick: () => setFallbackText(null), title: "\u0625\u063A\u0644\u0627\u0642", "aria-label": "\u0625\u063A\u0644\u0627\u0642", children: "\u00D7" }), _jsx("h2", { className: "a11y-title", children: XR_LOG_COPY_LABEL }), _jsx("p", { className: "a11y-sub", children: XR_LOG_FALLBACK_HINT }), _jsx("textarea", { className: "a11y-fallback-textarea", value: fallbackText, readOnly: true, autoFocus: true, onFocus: (e) => e.currentTarget.select(), rows: 16 })] }) }))] }));
}
